import { Fragment, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { AccountAppSettingRow } from '@/lib/types/feature/account-app-setting';

interface AccountAppSettingsTableProps {
  rows: AccountAppSettingRow[];
}

interface CategoryGroup {
  category: string;
  rows: AccountAppSettingRow[];
}

/**
 * 設定値をカテゴリ見出し付きで並べる。
 *
 * カテゴリは rowSpan でまとめず見出し行で区切る。CSVが1行=1設定の縦持ちなので、
 * 画面も同じ構造にしておくと「画面とCSVで内容が違う」が起きない。
 */
export function AccountAppSettingsTable({ rows }: AccountAppSettingsTableProps) {
  // カテゴリの並びはバックエンドの定義順をそのまま使う（意味のある順序になっている）
  const groups = useMemo<CategoryGroup[]>(() => {
    const result: CategoryGroup[] = [];
    for (const row of rows) {
      const last = result[result.length - 1];
      if (last && last.category === row.category) {
        last.rows.push(row);
      } else {
        result.push({ category: row.category, rows: [row] });
      }
    }
    return result;
  }, [rows]);

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '28%' }}>設定項目</TableCell>
            <TableCell sx={{ width: '32%' }}>値</TableCell>
            <TableCell>説明</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => (
            <Fragment key={group.category}>
              <TableRow>
                <TableCell colSpan={3} sx={{ bgcolor: 'action.hover', fontWeight: 600 }}>
                  {group.category}
                </TableCell>
              </TableRow>
              {group.rows.map((row) => (
                <TableRow key={row.key} hover>
                  <TableCell>{row.label}</TableCell>
                  <TableCell
                    sx={{
                      // 値が無い行は薄く出す（設定値そのものと見分けられるように）
                      color: row.value_state === 'set' ? 'text.primary' : 'text.disabled',
                      fontWeight: row.value_state === 'set' ? 500 : 400,
                    }}
                  >
                    {row.value}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.description}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {row.source}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
